import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { socket } from "../api"; // make sure socket is connected to your backend
import Logo from "../assets/mobile.png"; // same logo as signup page
import { X } from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { parseJWT } from "../utils/jwtUtils";

const Login = () => {
  const [formData, setFormData] = useState({ phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showTimerModal, setShowTimerModal] = useState(false);
  const [timeLeft, setTimeLeft] = useState({
    days: 10,
    hours: 3,
    minutes: 55,
    seconds: 0,
  });
  const navigate = useNavigate();

  /* -----------------------------------------
      REAL LIVE COUNTDOWN LOGIC 
  ----------------------------------------- */
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        let { days, hours, minutes, seconds } = prev;

        if (seconds > 0) {
          return { ...prev, seconds: seconds - 1 };
        }

        if (minutes > 0) {
          return { ...prev, minutes: minutes - 1, seconds: 59 };
        }

        if (hours > 0) {
          return { ...prev, hours: hours - 1, minutes: 59, seconds: 59 };
        }

        if (days > 0) {
          return {
            ...prev,
            days: days - 1,
            hours: 23,
            minutes: 59,
            seconds: 59,
          };
        }

        // countdown finished
        clearInterval(interval);
        return prev;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogin = (e) => {
    e.preventDefault();
    setLoading(true);

    socket.emit("login", {
      phone: formData.phone.trim(),
      password: formData.password.trim(),
    });

    socket.once("loginResponse", (data) => {
      setLoading(false);
      if (data.success) {
        const userInfo = parseJWT(data?.token);

        localStorage.setItem(
          "userInfo",
          JSON.stringify({
            token: data.token,
          }),
        );

        toast.success("Successfully logged in!");

        // Redirect based on role after small delay to show toast
        setTimeout(() => {
          if (userInfo.role === "admin" || userInfo.role === "super_admin") {
            navigate("/admin-page"); // admin route
          } else {
            navigate("/"); // regular user home
          }
        }, 1000);
      } else {
        toast.error(data.message || "Login failed!");
      }
    });
  };

  const [showPassword, setShowPassword] = useState(false);
  const togglePasswordVisibility = () => {
    setShowPassword((prevState) => !prevState);
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#020617]">
      <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-xl w-full max-w-md p-8 text-white border border-white/20 mx-4">
        <div className="flex flex-col items-center mb-6">
          <div class="flex flex-col p-4 space-y-1 text-center">
            <div class="flex items-center justify-center">
              <div class="w-16 h-16 bg-gradient-to-br from-red-500 to-cyan-600 rounded-full flex items-center justify-center shadow-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-log-in w-8 h-8 text-white"
                >
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                  <polyline points="10 17 15 12 10 7"></polyline>
                  <line x1="15" x2="3" y1="12" y2="12"></line>
                </svg>
              </div>
            </div>
          </div>
          <h2 className="text-2xl font-semibold tracking-wide text-center font-serif">
            Bingo.EnatGames.com
          </h2>
          <p className="text-gray-300 text-sm mt-2 font-serif">
            ለቴሌ ሲም 09... ለሳፋሪ 07... ይጠቀሙ
          </p>
        </div>

        <form onSubmit={handleLogin} className="space-y-5">
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-200 font-serif">
              Phone Number
            </label>
            <input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="09xxxxxxxx"
              className="w-full p-3 rounded-xl bg-white/10 text-white border border-gray-500 focus:ring-2 focus:ring-blue-400 outline-none placeholder-gray-400"
              required
            />
          </div>

          <div>
            <label className="block mb-1 text-sm font-medium text-gray-200 font-serif">
              Password
            </label>
            <div className="relative flex items-center">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="********"
                className="w-full p-3 rounded-xl bg-white/10 text-white border border-gray-500 focus:ring-2 focus:ring-blue-400 outline-none placeholder-gray-400"
                required
              />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill={showPassword ? "#000000" : " #3E3ECE"} // Dynamic color based on state
                className="w-[18px] h-[18px] absolute right-2 cursor-pointer"
                onClick={togglePasswordVisibility}
                viewBox="0 0 128 128"
              >
                <path d="M64 16c-32 0-58.4 20.8-64 48 5.6 27.2 32 48 64 48s58.4-20.8 64-48c-5.6-27.2-32-48-64-48zm0 80c-17.6 0-32-14.4-32-32s14.4-32 32-32 32 14.4 32 32-14.4 32-32 32zm0-48c-8.8 0-16 7.2-16 16s7.2 16 16 16 16-7.2 16-16-7.2-16-16-16z" />
              </svg>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className={`w-full p-3 rounded-xl font-semibold transition duration-300 font-serif ${
              loading
                ? "bg-gray-500 cursor-not-allowed"
                : "bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800"
            }`}
          >
            {loading ? "Login..." : "Log in"}
          </button>
        </form>

        <div className="text-center text-gray-300 text-sm mt-6 font-serif">
          መለያ የለዎትም?{" "}
          <span
            onClick={() => navigate("/signup")}
            className="text-blue-400 cursor-pointer hover:underline font-serif text-lg"
          >
            Sign Up
          </span>
          <a
            href="https://t.me/BingoEnatBot"
            target="_blank"
            rel="noopener noreferrer"
            className="block mt-2 cursor-pointer text-gray-100 hover:text-white transition duration-200 text-sm hover:underline"
          >
            Forgot Password
          </a>
        </div>
      </div>

      {/* LIMITED TIME OFFER MODAL */}
      {showTimerModal && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-20"></div>
          <div className="fixed inset-0 flex items-center justify-center z-30 px-4">
            <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-xl w-full max-w-md p-6 text-white border border-white/20">
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-bold text-lg font-serif">
                  Limited Time Offer
                </h3>
                <button
                  onClick={() => setShowTimerModal(false)}
                  className="text-white hover:bg-white/20 p-1 rounded transition"
                >
                  <X size={22} />
                </button>
              </div>

              {/* Timer */}
              <div className="grid grid-cols-4 gap-2 mb-6">
                {[
                  { value: timeLeft.days, label: "ቀን" },
                  { value: timeLeft.hours, label: "ሰዓት" },
                  { value: timeLeft.minutes, label: "ደቂቃ" },
                  { value: timeLeft.seconds, label: "ሰከንድ" },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className="bg-white/10 border border-white/20 rounded-lg p-3 text-center"
                  >
                    <div className="text-2xl font-bold font-mono">
                      {String(item.value).padStart(2, "0")}
                    </div>
                    <div className="text-xs font-semibold text-gray-300 mt-2">
                      {item.label}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mb-6 bg-white/10 backdrop-blur-md rounded-lg h-28 flex items-center justify-center text-center px-4">
                <p className="text-sm font-semibold text-white font-serif">
                  🎁 Special Bonus Offer 🎁
                </p>
              </div>

              <ul className="space-y-3 text-sm text-gray-300">
                <li className="flex items-start gap-3 font-serif">
                  <span className="font-bold text-lg leading-5">•</span>
                  <span>1 ጨዋታ = 1 ነጥብ</span>
                </li>
                <li className="flex items-start gap-3 font-serif">
                  <span className="font-bold text-lg leading-5">•</span>
                  <span>ተጫውተው ብቻ ይሸለሙ</span>
                </li>
                <li className="flex items-start gap-3 font-serif">
                  <span className="font-bold text-lg leading-5">•</span>
                  <span>
                    ለመከታተል{" "}
                    <a
                      href="/leaderboard"
                      className="text-blue-400 hover:underline font-bold font-serif"
                    >
                      Leaderboard
                    </a>{" "}
                    ይግቡ
                  </span>
                </li>
                <li className="flex items-start gap-3 font-serif">
                  <span className="font-bold text-lg leading-5">•</span>
                  <span>
                    የቴሌግራም ግሩፋችንን ይቀላቀሉ{" "}
                    <a
                      href="https://t.me/+7Hd1n0wcN1o2YzBk"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:underline font-bold"
                    >
                      ይህን ይጫኑ
                    </a>
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </>
      )}

      {/* Toast Popup */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        pauseOnHover
        draggable
        theme="colored"
      />
    </div>
  );
};

export default Login;
